import { Link } from "react-router-dom";
import { Stethoscope, Truck, Pill, Check, Sparkles, ArrowRight, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/app/Logo";

const PARTNERS = [
  {
    key: "medecin",
    title: "Vous êtes professionnel de santé",
    subtitle: "Médecins, spécialistes, sages-femmes, kinés, orthophonistes, IDE…",
    color: "primary",
    icon: Stethoscope,
    gradient: "from-[#618DB8] to-[#2A4365]",
    perks: [
      "Réseau social pro sécurisé HDS",
      "Fiche pro SEO visible sur Google",
      "Agenda + RDV en ligne (Standard 69 € / mois)",
      "Téléconsultation HDS (Whereby)",
      "Messagerie chiffrée E2E confrères + patients",
      "Ordonnance numérique VIDAL (Premium)",
      "Formation continue certifiante",
      "30 jours d'essai gratuit sans CB",
    ],
    cta: "Créer mon compte médecin",
    ctaLink: "/register?role=doctor",
  },
  {
    key: "transporteur",
    title: "Vous êtes transporteur sanitaire",
    subtitle: "Ambulances, VSL, taxis conventionnés · agréés ARS uniquement",
    color: "emerald",
    icon: Truck,
    gradient: "from-emerald-600 to-emerald-800",
    perks: [
      "Marketplace transport sanitaire nationale",
      "Missions dispatchées automatiquement",
      "Bon de Transport électronique signé eIDAS",
      "Tiers payant CPAM automatique",
      "GPS temps réel + tracking patient",
      "0 commission les 3 premiers mois",
      "App mobile transporteur (PWA)",
      "Vérification agrément ARS en 48 h",
    ],
    cta: "Rejoindre le réseau Doctobook Trans",
    ctaLink: "/partenaires/pre-inscription?type=transport",
    badge: "Bientôt disponible · Pilote Var/PACA",
  },
  {
    key: "pharmacie",
    title: "Vous êtes pharmacie d'officine",
    subtitle: "Titulaire d'officine agréée ARS · préparation & livraison",
    color: "coral",
    icon: Pill,
    gradient: "from-[#FF7A5C] to-[#a8421f]",
    perks: [
      "Marketplace livraison médicaments",
      "Ordonnance numérique reçue directement",
      "Choix du mode : click&collect / livraison propre / transporteur agréé",
      "Paiement direct au pro (lien CB, virement) OU Carte Vitale (Icanopée)",
      "Stock temps réel visible",
      "Commission plateforme : 3 % à 5 % HT",
      "Fiche officine dans annuaire national",
      "Accès à la communauté professionnelle",
    ],
    cta: "Rejoindre le réseau Doctobook Rx",
    ctaLink: "/partenaires/pre-inscription?type=pharmacy",
    badge: "Bientôt disponible · Pilote Q2 2026",
  },
];

export default function Partenaires() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-100 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Logo /></Link>
          <div className="flex gap-2">
            <Link to="/tarifs"><Button variant="ghost" size="sm">Tarifs</Button></Link>
            <Link to="/login"><Button variant="ghost" size="sm">Se connecter</Button></Link>
          </div>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-6 py-16 text-center">
        <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide mb-4">
          <Sparkles className="h-3 w-3" />Programme partenaires 2026
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-deep tracking-tight leading-[1.05]">
          Rejoignez l'écosystème santé Doctobook
        </h1>
        <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
          Une plateforme unique qui réunit médecins, transporteurs sanitaires et pharmacies pour offrir aux patients français
          un parcours de soin fluide, sécurisé et 100 % hébergé HDS en France.
        </p>
        <div className="mt-6 inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 text-xs text-slate-600 font-semibold">
          <Shield className="h-3.5 w-3.5 text-emerald-600" />
          Association loi 1901 · SIRET 922 912 126 00013 · Hébergement OVHcloud HDS France
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16 grid gap-6 md:grid-cols-3">
        {PARTNERS.map((p) => {
          const Icon = p.icon;
          return (
            <article key={p.key} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition" data-testid={`partner-${p.key}`}>
              <div className={`bg-gradient-to-br ${p.gradient} p-6 text-white`}>
                <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center mb-3">
                  <Icon className="h-6 w-6" />
                </div>
                <h2 className="font-display text-xl font-extrabold leading-tight">{p.title}</h2>
                <p className="text-sm text-white/85 mt-1">{p.subtitle}</p>
                {p.badge && (
                  <span className="mt-3 inline-block bg-white/15 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">
                    {p.badge}
                  </span>
                )}
              </div>
              <div className="p-6">
                <ul className="space-y-2.5 mb-6">
                  {p.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>
                <Link to={p.ctaLink}>
                  <Button
                    className={`w-full font-semibold ${
                      p.color === "coral" ? "btn-coral" :
                      p.color === "emerald" ? "bg-emerald-600 hover:bg-emerald-700 text-white" :
                      "btn-primary"
                    }`}
                    data-testid={`cta-${p.key}`}
                  >
                    {p.cta}<ArrowRight className="h-4 w-4 ms-1.5" />
                  </Button>
                </Link>
              </div>
            </article>
          );
        })}
      </section>

      <section className="max-w-4xl mx-auto px-6 pb-16">
        <h2 className="font-display text-2xl font-bold text-deep mb-6 text-center">Comment ça marche</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {[
            { n: "1", title: "Créez votre profil", desc: "Inscription gratuite avec vérification RPPS / agrément ARS / diplôme selon votre métier." },
            { n: "2", title: "Vérification en 48 h", desc: "Notre équipe contrôle les pièces justificatives et valide votre profil professionnel." },
            { n: "3", title: "Commencez à travailler", desc: "Recevez vos premières missions, patients ou ordonnances via notre plateforme." },
          ].map((s) => (
            <div key={s.n} className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="w-10 h-10 rounded-full bg-[#618DB8] text-white flex items-center justify-center font-extrabold mb-3">{s.n}</div>
              <h3 className="font-bold text-deep mb-1">{s.title}</h3>
              <p className="text-sm text-slate-600">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-100 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-8 text-xs text-slate-500 flex flex-wrap gap-4 justify-between">
          <div>© Association DOCTOBOOK · SIRET 922 912 126 00013 · RNA W832021177</div>
          <div className="flex gap-4">
            <Link to="/tarifs" className="hover:text-slate-800">Tarifs</Link>
            <Link to="/legal/mentions-legales" className="hover:text-slate-800">Mentions légales</Link>
            <Link to="/legal/cgu" className="hover:text-slate-800">CGU</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
